import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import {
  AlertTriangle,
  Check,
  FileText,
  Image as ImageIcon,
  MapPin,
  Plus,
  ShieldAlert,
  Users,
  X,
} from 'lucide-react';
import { toast } from 'sonner';

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';

const TZ = 'America/Mexico_City';
// Valor del select cuando el usuario elige capturar un título propio en lugar de
// tomarlo del catálogo. No es un id real: al enviar se manda title_id en null.
const OTRO_TITULO = 'OTRO';
const TITLE_MAX = 100;
const DESCRIPTION_MAX = 120;
const DETAIL_MAX = 250;
const MAX_PHOTOS = 2;
const MAX_IMAGE_SIZE_MB = 5;
const MAX_INSTITUCIONES_PARTICIPANTES = 3;
const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY_FORM = {
  title_id: '',
  title: '',
  description: '',
  detalle_actividad: '',
  start_date: '',
  end_date: '',
  alcaldia_id: '',
  territorio_id: '',
  poligono_id: '',
  calle_referencia: '',
  institucion_principal_id: '',
  linea_principal_id: '',
  lineas_ramificadas_ids: [],
  tipo_actividad_id: '',
  tipo_visita_id: '',
  actividad_conjunta: 'NO',
  institucion_participante_temp: '',
  instituciones_participantes_ids: [],
  poblacion_objetivo_id: '',
  poblaciones_objetivo_ids: [],
  responsable_nombre: '',
  total_visitas: '0',
  total_asistentes: '0',
  total_beneficiados: '',
  progress_percentage: '',
  resultados: '',
  observaciones: '',
};

const getTrimmedLength = (value = '') => value.trim().length;

const toLocalDateString = (date) => {
  const formatter = new Intl.DateTimeFormat('en-CA', {
    timeZone: TZ,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
  return formatter.format(date);
};

const getMexicoCityDateParts = (date = new Date()) => {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: TZ,
    weekday: 'short',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).formatToParts(date);

  const read = (type) => parts.find((p) => p.type === type)?.value;
  return {
    weekday: read('weekday'),
    year: Number(read('year')),
    month: Number(read('month')),
    day: Number(read('day')),
    hour: Number(read('hour')),
    minute: Number(read('minute')),
  };
};

const getDeadlineStatus = (date = new Date()) => {
  const mexico = getMexicoCityDateParts(date);
  const weekMap = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };
  const weekday = weekMap[mexico.weekday] ?? 0;

  const currentMidnightUtc = Date.UTC(mexico.year, mexico.month - 1, mexico.day);
  const distanceToSaturday = 6 - weekday;
  const saturdayUtc = currentMidnightUtc + distanceToSaturday * 24 * 60 * 60 * 1000;
  const deadlineUtc = saturdayUtc + 12 * 60 * 60 * 1000;
  const nowProxyUtc = Date.UTC(
    mexico.year,
    mexico.month - 1,
    mexico.day,
    mexico.hour,
    mexico.minute
  );

  const diffMs = deadlineUtc - nowProxyUtc;
  const isClosed = diffMs <= 0;
  const remainingHours = diffMs / (1000 * 60 * 60);
  const isWarning = !isClosed && remainingHours <= 2;

  return {
    isClosed,
    isWarning,
    deadlineText: 'Cierre semanal: sábado a las 12:00 horas (CDMX).',
    remainingText: isClosed
      ? 'El corte semanal ya ocurrió. Las actividades capturadas ahora se considerarían captura tardía o para el siguiente corte, según la lógica de backend.'
      : isWarning
        ? 'Quedan menos de 2 horas para el corte semanal.'
        : 'Las actividades capturadas antes del corte entrarían al reporte de la semana en curso.',
  };
};

const MultiSelectChecklist = ({
  label,
  items,
  values,
  onToggle,
  helperText,
  error,
  disabled = false,
}) => {
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-2">
        <Label>{label}</Label>
        {helperText ? <span className="text-xs text-slate-500">{helperText}</span> : null}
      </div>

      <div className={`grid gap-2 sm:grid-cols-2 ${disabled ? 'opacity-60' : ''}`}>
        {items.map((item) => {
          const checked = values.includes(String(item.id));
          return (
            <label
              key={item.id}
              className={`flex items-start gap-3 rounded-lg border p-3 text-sm transition-colors ${checked ? 'border-[#8B1C4A] bg-[#8B1C4A]/5' : 'border-slate-200 bg-white'
                } ${disabled ? 'cursor-not-allowed' : 'cursor-pointer hover:bg-slate-50'}`}
            >
              <input
                type="checkbox"
                className="mt-1 accent-[#8B1C4A]"
                checked={checked}
                disabled={disabled}
                onChange={() => onToggle(String(item.id))}
              />
              <span>{item.name}</span>
            </label>
          );
        })}
      </div>

      {error ? <p className="text-sm text-red-600">{error}</p> : null}
    </div>
  );
};

const MultiSelectDropdown = ({
  label,
  items,
  values,
  onToggle,
  onSelectAll,
  onClearAll,
  helperText,
  error,
  disabled = false,
  placeholder = 'Selecciona una o varias opciones',
}) => {
  const [open, setOpen] = useState(false);

  const selectedLabels = items
    .filter((item) => values.includes(String(item.id)))
    .map((item) => item.name);

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-2">
        <Label>{label}</Label>
        {helperText ? <span className="text-xs text-slate-500">{helperText}</span> : null}
      </div>

      <div className="relative">
        <button
          type="button"
          onClick={() => !disabled && setOpen((prev) => !prev)}
          className={`flex min-h-[44px] w-full items-center justify-between rounded-md border bg-white px-3 py-2 text-left text-sm ${error ? 'border-red-500' : 'border-input'
            } ${disabled ? 'cursor-not-allowed opacity-60' : ''}`}
        >
          <span className={`block pr-3 ${selectedLabels.length ? 'text-slate-900' : 'text-slate-500'}`}>
            {selectedLabels.length ? selectedLabels.join(', ') : placeholder}
          </span>
          <span className="text-xs text-slate-500">{open ? 'Cerrar' : 'Abrir'}</span>
        </button>

        {open && !disabled ? (
          <div className="absolute z-20 mt-2 w-full rounded-md border bg-white p-3 shadow-lg">
            <div className="mb-3 flex flex-wrap gap-2 border-b pb-3">
              <Button type="button" variant="outline" size="sm" onClick={onSelectAll}>
                Seleccionar todo
              </Button>
              <Button type="button" variant="outline" size="sm" onClick={onClearAll}>
                Limpiar selección
              </Button>
            </div>

            <div className="max-h-64 space-y-2 overflow-auto">
              {items.map((item) => {
                const checked = values.includes(String(item.id));
                return (
                  <label
                    key={item.id}
                    className={`flex cursor-pointer items-start gap-3 rounded-lg border p-3 text-sm transition-colors ${checked ? 'border-[#8B1C4A] bg-[#8B1C4A]/5' : 'border-slate-200 bg-white'
                      }`}
                  >
                    <input
                      type="checkbox"
                      className="mt-1 accent-[#8B1C4A]"
                      checked={checked}
                      onChange={() => onToggle(String(item.id))}
                    />
                    <span>{item.name}</span>
                  </label>
                );
              })}
            </div>
          </div>
        ) : null}
      </div>

      {selectedLabels.length ? (
        <div className="flex flex-wrap gap-2 pt-1">
          {selectedLabels.map((labelItem) => (
            <span
              key={labelItem}
              className="rounded-full border border-[#8B1C4A]/30 bg-[#8B1C4A]/5 px-3 py-1 text-xs text-slate-700"
            >
              {labelItem}
            </span>
          ))}
        </div>
      ) : null}

      {error ? <p className="text-sm text-red-600">{error}</p> : null}
    </div>
  );
};

const SelectedInstituciones = ({ ids, instituciones, onRemove }) => {
  if (!ids.length) return null;

  const selected = ids
    .map((id) => instituciones.find((inst) => String(inst.id) === String(id)))
    .filter(Boolean);

  return (
    <div className="space-y-2">
      <p className="text-sm font-medium text-slate-700">Dependencias participantes seleccionadas</p>
      <div className="flex flex-wrap gap-2">
        {selected.map((item) => (
          <div
            key={item.id}
            className="inline-flex items-center gap-2 rounded-full border border-[#8B1C4A]/30 bg-[#8B1C4A]/5 px-3 py-2 text-sm text-slate-700"
          >
            <span>{item.name}</span>
            <button
              type="button"
              className="rounded-full p-0.5 text-[#8B1C4A] hover:bg-[#8B1C4A]/10"
              onClick={() => onRemove(String(item.id))}
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};

export const CargarActividad = () => {
  const navigate = useNavigate();
  const { id } = useParams();

  const isEditMode = Boolean(id);
  const [loading, setLoading] = useState(false);
  const [catalogLoading, setCatalogLoading] = useState(true);
  const [errors, setErrors] = useState({});
  const [currentUser, setCurrentUser] = useState(null);

  const [dependencies, setDependencies] = useState([]);
  const [alcaldiasCatalog, setAlcaldiasCatalog] = useState([]);
  const [territoriesCatalog, setTerritoriesCatalog] = useState([]);
  const [polygonsCatalog, setPolygonsCatalog] = useState([]);
  const [axesCatalog, setAxesCatalog] = useState([]);
  const [subaxesCatalog, setSubaxesCatalog] = useState([]);
  const [activityTypesCatalog, setActivityTypesCatalog] = useState([]);
  const [visitTypesCatalog, setVisitTypesCatalog] = useState([]);
  const [targetPopulationsCatalog, setTargetPopulationsCatalog] = useState([]);
  const [activityTitlesCatalog, setActivityTitlesCatalog] = useState([]);
  // En edición, el título guardado puede ya no estar en el catálogo activo
  // (se desactivó). Se conserva aparte para no dejar el select en blanco.
  const [editingTitleOption, setEditingTitleOption] = useState(null);

  const [formData, setFormData] = useState({
    ...EMPTY_FORM,
    start_date: toLocalDateString(new Date()),
    end_date: toLocalDateString(new Date()),
    responsable_nombre: '',
    institucion_principal_id: '',
  });

  const [uploadedPhotos, setUploadedPhotos] = useState([]);

  const token = localStorage.getItem('token');

  const alcaldias = alcaldiasCatalog;
  const instituciones = dependencies;
  const lineasPrincipales = axesCatalog;
  const tiposActividad = activityTypesCatalog;
  const tiposVisita = visitTypesCatalog;
  const poblaciones = targetPopulationsCatalog;

  const territorios = useMemo(() => {
    return territoriesCatalog.filter(
      (item) => String(item.alcaldia_id) === String(formData.alcaldia_id)
    );
  }, [territoriesCatalog, formData.alcaldia_id]);

  const poligonos = useMemo(() => {
    return polygonsCatalog.filter(
      (item) => String(item.territory_id) === String(formData.territorio_id)
    );
  }, [polygonsCatalog, formData.territorio_id]);

  const selectedTipoActividad = useMemo(() => {
    return tiposActividad.find((item) => String(item.id) === String(formData.tipo_actividad_id));
  }, [tiposActividad, formData.tipo_actividad_id]);

  const isFeriaFestival = useMemo(() => {
    const name = selectedTipoActividad?.name?.toUpperCase?.() || '';
    return name.includes('FERIA') || name.includes('FESTIVAL');
  }, [selectedTipoActividad]);

  const isVisita = useMemo(() => {
    const name = selectedTipoActividad?.name?.toUpperCase?.() || '';
    return name === 'VISITA';
  }, [selectedTipoActividad]);

  const isObraProyecto = useMemo(() => {
    const name = selectedTipoActividad?.name?.toUpperCase?.() || '';
    return name.includes('OBRA') || name.includes('PROYECTO');
  }, [selectedTipoActividad]);

  const lineasRamificadasDisponibles = useMemo(() => {
    return subaxesCatalog.filter(
      (item) => String(item.axis_id) === String(formData.linea_principal_id)
    );
  }, [subaxesCatalog, formData.linea_principal_id]);

  const institucionesParticipantesDisponibles = useMemo(() => {
    return instituciones.filter((item) => {
      const isPrincipal = String(item.id) === String(formData.institucion_principal_id);
      const alreadySelected = formData.instituciones_participantes_ids.includes(String(item.id));
      return !isPrincipal && !alreadySelected;
    });
  }, [instituciones, formData.institucion_principal_id, formData.instituciones_participantes_ids]);

  const titulosDisponibles = useMemo(() => {
    const options = [...activityTitlesCatalog];

    // Si el título que trae la actividad en edición ya no viene en el catálogo
    // (porque se desactivó), se agrega para que el select lo pueda mostrar.
    if (
      editingTitleOption &&
      !options.some((item) => String(item.id) === String(editingTitleOption.id))
    ) {
      options.push(editingTitleOption);
    }

    return options;
  }, [activityTitlesCatalog, editingTitleOption]);

  const deadlineStatus = useMemo(() => getDeadlineStatus(), []);
  const photoCount = uploadedPhotos.length;

  useEffect(() => {
    const fetchCatalogs = async () => {
      try {
        setCatalogLoading(true);

        const config = token
          ? {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
          : {};

        const [
          dependenciesRes,
          alcaldiasRes,
          territoriesRes,
          polygonsRes,
          axesRes,
          subaxesRes,
          activityTypesRes,
          visitTypesRes,
          targetPopulationsRes,
          meRes,
        ] = await Promise.all([
          axios.get(`${API}/dependencies`, config),
          axios.get(`${API}/alcaldias`, config),
          axios.get(`${API}/territories`, config),
          axios.get(`${API}/territory-polygons`, config),
          axios.get(`${API}/axes`, config),
          axios.get(`${API}/subaxes`, config),
          axios.get(`${API}/activity-types`, config),
          axios.get(`${API}/visit-types`, config),
          axios.get(`${API}/target-populations`, config),
          axios.get(`${API}/auth/me`, config),
        ]);

        setDependencies(dependenciesRes.data || []);
        setAlcaldiasCatalog(alcaldiasRes.data || []);
        setTerritoriesCatalog(territoriesRes.data || []);
        setPolygonsCatalog(polygonsRes.data || []);
        setAxesCatalog(axesRes.data || []);
        setSubaxesCatalog(subaxesRes.data || []);
        setActivityTypesCatalog(activityTypesRes.data || []);
        setVisitTypesCatalog(visitTypesRes.data || []);
        setTargetPopulationsCatalog(targetPopulationsRes.data || []);
        setCurrentUser(meRes.data || null);
      } catch (error) {
        console.error('Error cargando catálogos:', error);
        toast.error('No se pudieron cargar catálogos o usuario.');
      } finally {
        setCatalogLoading(false);
      }
    };

    fetchCatalogs();
  }, [token]);

  useEffect(() => {
    if (!isEditMode || !token || !territoriesCatalog.length) return;

    const fetchActivityDetail = async () => {
      try {
        setLoading(true);

        const response = await axios.get(`${API}/activities/${id}`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        const activity = response.data || {};
        setFormData(mapActivityToForm(activity));

        if (activity.title_id) {
          setEditingTitleOption({
            id: String(activity.title_id),
            name: activity.title || 'Título sin nombre',
          });
        }
      } catch (error) {
        console.error(error);
        toast.error('No fue posible cargar la actividad para edición.');
      } finally {
        setLoading(false);
      }
    };

    fetchActivityDetail();
  }, [id, isEditMode, token, territoriesCatalog]);

  useEffect(() => {
    if (!currentUser) return;

    setFormData((prev) => ({
      ...prev,
      responsable_nombre: prev.responsable_nombre || currentUser.name || '',
      institucion_principal_id:
        prev.institucion_principal_id || currentUser.dependency_id || '',
    }));
  }, [currentUser]);

  // El catálogo de títulos se acota a la dependencia de la actividad, que es la
  // del usuario autenticado (el campo es de solo lectura). El backend exige esa
  // misma correspondencia al guardar.
  useEffect(() => {
    const dependencyId = formData.institucion_principal_id;

    if (!dependencyId || !token) {
      setActivityTitlesCatalog([]);
      return;
    }

    const fetchActivityTitles = async () => {
      try {
        const response = await axios.get(`${API}/activity-titles`, {
          headers: { Authorization: `Bearer ${token}` },
          params: { dependency_id: dependencyId },
        });
        setActivityTitlesCatalog(response.data || []);
      } catch (error) {
        console.error('Error cargando títulos de actividad:', error);
        toast.error('No se pudieron cargar los títulos de actividad.');
        setActivityTitlesCatalog([]);
      }
    };

    fetchActivityTitles();
  }, [formData.institucion_principal_id, token]);

  useEffect(() => {
    if (poligonos.length === 1 && !formData.poligono_id) {
      setFormData((prev) => ({ ...prev, poligono_id: String(poligonos[0].id) }));
    }
  }, [poligonos, formData.poligono_id]);

  useEffect(() => {
    return () => {
      uploadedPhotos.forEach((photo) => {
        if (photo.preview) URL.revokeObjectURL(photo.preview);
      });
    };
  }, [uploadedPhotos]);

  const validateField = (name, value, draft = formData) => {
    switch (name) {
      case 'title_id':
        if (!value) return 'El título es obligatorio.';
        return '';

      case 'title':
        // Sólo se captura cuando se eligió "Otro"; con un título del catálogo
        // este campo va vacío a propósito.
        if (draft.title_id !== OTRO_TITULO) return '';
        if (!getTrimmedLength(value)) return 'Escribe el título de la actividad.';
        if (getTrimmedLength(value) > TITLE_MAX) return `Máximo ${TITLE_MAX} caracteres.`;
        return '';

      case 'description':
        if (!getTrimmedLength(value)) return 'La descripción es obligatoria.';
        if (getTrimmedLength(value) > DESCRIPTION_MAX) return `Máximo ${DESCRIPTION_MAX} caracteres.`;
        return '';

      case 'detalle_actividad':
        if (getTrimmedLength(value) > DETAIL_MAX) return `Máximo ${DETAIL_MAX} caracteres.`;
        return '';

      case 'start_date':
        if (!value) return 'La fecha de inicio es obligatoria.';
        return '';

      case 'end_date':
        if (!value) return 'La fecha de cierre es obligatoria.';
        if (draft.start_date && value < draft.start_date) {
          return 'La fecha de cierre no puede ser menor a la fecha de inicio.';
        }
        return '';

      case 'alcaldia_id':
        return value ? '' : 'Selecciona una alcaldía.';

      case 'territorio_id':
        return value ? '' : 'Selecciona un territorio.';

      case 'poligono_id':
        if (!formData.territorio_id) return 'Selecciona un territorio.';
        if (poligonos.length === 0) return '';
        return value ? '' : 'Selecciona un polígono.';

      case 'institucion_principal_id':
        return value ? '' : 'La dependencia principal es obligatoria.';

      case 'linea_principal_id':
        return value ? '' : 'Selecciona una línea principal.';

      case 'lineas_ramificadas_ids':
        return draft.lineas_ramificadas_ids.length > 0
          ? ''
          : 'Selecciona una línea de acción / rama.';

      case 'tipo_actividad_id':
        return value ? '' : 'Selecciona un tipo de actividad.';

      case 'tipo_visita_id':
        if (isVisita && !value) return 'Selecciona el tipo de visita.';
        return '';

      case 'poblacion_objetivo_id':
        if (!isFeriaFestival && !value) return 'Selecciona la población objetivo.';
        return '';

      case 'poblaciones_objetivo_ids':
        if (isFeriaFestival && draft.poblaciones_objetivo_ids.length === 0) {
          return 'Selecciona al menos una población objetivo.';
        }
        return '';

      case 'responsable_nombre':
        return value?.trim() ? '' : 'El enlace responsable es obligatorio.';

      case 'calle_referencia':
        if (!getTrimmedLength(value)) return 'La calle o referencia es obligatoria.';
        return '';

      case 'instituciones_participantes_ids':
        if (draft.actividad_conjunta === 'SÍ' && draft.instituciones_participantes_ids.length === 0) {
          return 'Selecciona al menos una dependencia participante.';
        }
        if (draft.instituciones_participantes_ids.length > MAX_INSTITUCIONES_PARTICIPANTES) {
          return `Solo puedes agregar hasta ${MAX_INSTITUCIONES_PARTICIPANTES} dependencias participantes.`;
        }
        return '';

      case 'metricas': {
        const optionalMetricFields = ['total_visitas', 'total_asistentes'];
        const hasInvalidOptionalMetric = optionalMetricFields.some((field) => {
          if (draft[field] === '' || draft[field] === null || draft[field] === undefined) return false;
          return Number(draft[field]) < 0 || Number.isNaN(Number(draft[field]));
        });

        if (hasInvalidOptionalMetric) {
          return 'Visitas y asistentes deben ser números iguales o mayores a 0.';
        }

        if (
          draft.total_beneficiados === '' ||
          draft.total_beneficiados === null ||
          draft.total_beneficiados === undefined
        ) {
          return 'Total de beneficiados es obligatorio. Debes escribir un valor, incluso 0.';
        }

        if (Number(draft.total_beneficiados) < 0 || Number.isNaN(Number(draft.total_beneficiados))) {
          return 'Total de beneficiados debe ser un número igual o mayor a 0.';
        }

        if (isObraProyecto) {
          if (
            draft.progress_percentage === '' ||
            draft.progress_percentage === null ||
            draft.progress_percentage === undefined
          ) {
            return 'El porcentaje de avance es obligatorio para obras y proyectos.';
          }

          const percent = Number(draft.progress_percentage);

          if (Number.isNaN(percent) || percent < 0 || percent > 100) {
            return 'El porcentaje de avance debe estar entre 0 y 100.';
          }
        }

        return '';
      }

      case 'resultados':
        if (!getTrimmedLength(value)) return 'Los resultados obtenidos son obligatorios.';
        return '';

      case 'photos':
        if (!isEditMode && uploadedPhotos.length === 0) return 'Debes agregar al menos una foto.';
        return '';

      default:
        return '';
    }
  };

  const updateField = (field, value) => {
    setFormData((prev) => {
      const next = { ...prev, [field]: value };

      if (field === 'alcaldia_id') {
        next.territorio_id = '';
        next.poligono_id = '';
      }

      if (field === 'territorio_id') {
        next.poligono_id = '';
      }

      if (field === 'linea_principal_id') {
        next.lineas_ramificadas_ids = [];
      }

      if (field === 'tipo_actividad_id') {
        next.tipo_visita_id = '';
        next.poblacion_objetivo_id = '';
        next.poblaciones_objetivo_ids = [];
        next.progress_percentage = '';
      }

      if (field === 'actividad_conjunta' && value === 'NO') {
        next.institucion_participante_temp = '';
        next.instituciones_participantes_ids = [];
      }

      if (field === 'title_id' && value !== OTRO_TITULO) {
        next.title = '';
      }

      if (field === 'institucion_principal_id') {
        next.instituciones_participantes_ids = next.instituciones_participantes_ids.filter(
          (idItem) => idItem !== value
        );
      }

      return next;
    });

    setErrors((prev) => ({ ...prev, [field]: '', metricas: '' }));
  };

  const addInstitucionParticipante = () => {
    if (!formData.institucion_participante_temp) {
      toast.error('Selecciona una dependencia participante.');
      return;
    }

    if (formData.instituciones_participantes_ids.length >= MAX_INSTITUCIONES_PARTICIPANTES) {
      toast.error(`Solo puedes agregar hasta ${MAX_INSTITUCIONES_PARTICIPANTES} dependencias.`);
      return;
    }

    setFormData((prev) => ({
      ...prev,
      instituciones_participantes_ids: [
        ...prev.instituciones_participantes_ids,
        prev.institucion_participante_temp,
      ],
      institucion_participante_temp: '',
    }));

    setErrors((prev) => ({ ...prev, instituciones_participantes_ids: '' }));
  };

  const removeInstitucionParticipante = (idToRemove) => {
    setFormData((prev) => ({
      ...prev,
      instituciones_participantes_ids: prev.instituciones_participantes_ids.filter(
        (item) => item !== idToRemove
      ),
    }));
  };

  const handlePhotoChange = (event) => {
    const files = Array.from(event.target.files || []);
    if (!files.length) return;

    if (uploadedPhotos.length >= MAX_PHOTOS) {
      toast.error(`Solo se permiten ${MAX_PHOTOS} fotos.`);
      event.target.value = '';
      return;
    }

    const availableSlots = MAX_PHOTOS - uploadedPhotos.length;
    const candidates = files.slice(0, availableSlots);

    if (files.length > availableSlots) {
      toast.error(`Solo puedes agregar ${availableSlots} foto(s) más.`);
    }

    const valid = [];
    for (const file of candidates) {
      if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
        toast.error(`${file.name}: formato no permitido.`);
        continue;
      }
      if (file.size > MAX_IMAGE_SIZE_MB * 1024 * 1024) {
        toast.error(`${file.name}: excede ${MAX_IMAGE_SIZE_MB}MB.`);
        continue;
      }
      valid.push({
        id: `${file.name}-${file.size}-${Date.now()}-${Math.random()}`,
        file,
        preview: URL.createObjectURL(file),
        name: file.name,
        type: file.type,
      });
    }

    if (valid.length) {
      setUploadedPhotos((prev) => [...prev, ...valid]);
      setErrors((prev) => ({ ...prev, photos: '' }));
    }

    event.target.value = '';
  };

  const removePhoto = (photoId) => {
    setUploadedPhotos((prev) => {
      const target = prev.find((photo) => photo.id === photoId);
      if (target?.preview) URL.revokeObjectURL(target.preview);
      return prev.filter((photo) => photo.id !== photoId);
    });
  };

  const uploadPhotosForActivity = async (activityId) => {
    if (!uploadedPhotos.length) return;

    const uploadConfig = {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    };
    const results = await Promise.allSettled(
      uploadedPhotos.map((photo) => {
        const form = new FormData();
        form.append('file', photo.file);
        return axios.post(`${API}/uploads/activity/${activityId}/photo`, form, uploadConfig);
      })
    );

    const failed = results.filter((result) => result.status === 'rejected');

    if (failed.length > 0) {
      failed.forEach((item, index) => {
        if (item.status === 'rejected') {
          console.error(`Error foto ${index + 1}:`, item.reason?.response?.data || item.reason);
        }
      });

      toast.error(`La actividad se guardó, pero ${failed.length} foto(s) no se pudieron subir.`);
    } else {
      toast.success('Fotos subidas correctamente.');
    }
  };


  const mapActivityToForm = (activity) => {
    const matchedTerritory = territoriesCatalog.find(
      (item) => String(item.id) === String(activity.territory_id)
    );

    const targetPopulationIds = Array.isArray(activity.target_population_ids)
      ? activity.target_population_ids.map(String)
      : [];

    return {
      ...EMPTY_FORM,
      // El backend devuelve title ya resuelto y title_id para saber de dónde
      // vino: con title_id se preselecciona el catálogo, sin él se trata como
      // título capturado con la opción "Otro".
      title_id: activity.title_id ? String(activity.title_id) : OTRO_TITULO,
      title: activity.title_id ? '' : activity.title || '',
      description: activity.description || '',
      detalle_actividad: activity.detalle_actividad || '',
      start_date: activity.start_date || '',
      end_date: activity.end_date || '',
      alcaldia_id: activity.alcaldia_id || matchedTerritory?.alcaldia_id || '',
      territorio_id: activity.territory_id || '',
      poligono_id: activity.territory_polygon_id || '',
      calle_referencia: activity.street_reference || '',
      institucion_principal_id: activity.dependency_id || '',
      linea_principal_id: activity.axis_id ? String(activity.axis_id) : '',
      lineas_ramificadas_ids: activity.subaxis_ids?.length
        ? [String(activity.subaxis_ids[0])]
        : [],
      tipo_actividad_id: activity.activity_type_id ? String(activity.activity_type_id) : '',
      tipo_visita_id: activity.visit_type_id ? String(activity.visit_type_id) : '',
      actividad_conjunta: activity.is_joint_activity ? 'SÍ' : 'NO',
      institucion_participante_temp: '',
      instituciones_participantes_ids: (activity.participant_dependency_ids || []).map(String),
      poblacion_objetivo_id: targetPopulationIds.length === 1 ? targetPopulationIds[0] : '',
      poblaciones_objetivo_ids: targetPopulationIds,
      responsable_nombre: activity.responsible_name || '',
      total_visitas:
        activity.total_visits !== null && activity.total_visits !== undefined
          ? String(activity.total_visits)
          : '0',
      total_asistentes:
        activity.total_attendees !== null && activity.total_attendees !== undefined
          ? String(activity.total_attendees)
          : '0',
      total_beneficiados:
        activity.total_beneficiaries !== null && activity.total_beneficiaries !== undefined
          ? String(activity.total_beneficiaries)
          : '',
      progress_percentage:
        activity.progress_percentage !== null && activity.progress_percentage !== undefined
          ? String(activity.progress_percentage)
          : '',
      resultados: activity.results || '',
      observaciones: activity.observations || '',
    };
  };

  const validateForm = () => {
    const nextErrors = {
      title_id: validateField('title_id', formData.title_id),
      title: validateField('title', formData.title),
      description: validateField('description', formData.description),
      detalle_actividad: validateField('detalle_actividad', formData.detalle_actividad),
      start_date: validateField('start_date', formData.start_date, formData),
      end_date: validateField('end_date', formData.end_date, formData),
      alcaldia_id: validateField('alcaldia_id', formData.alcaldia_id),
      territorio_id: validateField('territorio_id', formData.territorio_id),
      poligono_id: validateField('poligono_id', formData.poligono_id),
      calle_referencia: validateField('calle_referencia', formData.calle_referencia),
      institucion_principal_id: validateField(
        'institucion_principal_id',
        formData.institucion_principal_id
      ),
      linea_principal_id: validateField('linea_principal_id', formData.linea_principal_id),
      lineas_ramificadas_ids: validateField(
        'lineas_ramificadas_ids',
        formData.lineas_ramificadas_ids,
        formData
      ),
      tipo_actividad_id: validateField('tipo_actividad_id', formData.tipo_actividad_id),
      tipo_visita_id: validateField('tipo_visita_id', formData.tipo_visita_id, formData),
      poblacion_objetivo_id: validateField(
        'poblacion_objetivo_id',
        formData.poblacion_objetivo_id,
        formData
      ),
      poblaciones_objetivo_ids: validateField(
        'poblaciones_objetivo_ids',
        formData.poblaciones_objetivo_ids,
        formData
      ),
      responsable_nombre: validateField('responsable_nombre', formData.responsable_nombre),
      instituciones_participantes_ids: validateField(
        'instituciones_participantes_ids',
        formData.instituciones_participantes_ids,
        formData
      ),
      metricas: validateField('metricas', null, formData),
      resultados: validateField('resultados', formData.resultados),
      photos: validateField('photos'),
    };

    setErrors(nextErrors);
    return !Object.values(nextErrors).some(Boolean);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!validateForm()) {
      toast.error('Corrige los campos marcados antes de continuar.');
      return;
    }

    setLoading(true);

    let payload;

    try {
      const usaTituloPropio = formData.title_id === OTRO_TITULO;

      payload = {
        // Los dos campos son excluyentes: el backend rechaza que vengan ambos.
        title: usaTituloPropio ? formData.title.trim() : null,
        title_id: usaTituloPropio ? null : formData.title_id,
        description: formData.description.trim(),
        detalle_actividad: formData.detalle_actividad.trim(),
        start_date: formData.start_date,
        end_date: formData.end_date,

        alcaldia_id: formData.alcaldia_id,
        territory_id: formData.territorio_id,
        street_reference: formData.calle_referencia.trim(),

        dependency_id: formData.institucion_principal_id,
        axis_id: formData.linea_principal_id,
        activity_type_id: formData.tipo_actividad_id,
        visit_type_id: isVisita && formData.tipo_visita_id ? formData.tipo_visita_id : null,

        is_joint_activity: formData.actividad_conjunta === 'SÍ',
        subaxis_ids: formData.lineas_ramificadas_ids,
        participant_dependency_ids: formData.instituciones_participantes_ids,

        target_population_ids: isFeriaFestival
          ? formData.poblaciones_objetivo_ids
          : formData.poblacion_objetivo_id
            ? [formData.poblacion_objetivo_id]
            : [],

        total_visits: formData.total_visitas === '' ? 0 : Number(formData.total_visitas),
        total_attendees: formData.total_asistentes === '' ? 0 : Number(formData.total_asistentes),
        total_beneficiaries: Number(formData.total_beneficiados),

        results: formData.resultados.trim(),
        observations: formData.observaciones.trim(),
      };

      if (formData.poligono_id) {
        payload.territory_polygon_id = formData.poligono_id;
      }

      if (isObraProyecto && formData.progress_percentage !== '') {
        payload.progress_percentage = Number(formData.progress_percentage);

      };

      console.log('Payload real de actividad:', payload);

   const response = isEditMode
  ? await axios.put(`${API}/activities/${id}`, payload, {
      headers: { Authorization: `Bearer ${token}` },
    })
  : await axios.post(`${API}/activities`, payload, {
      headers: { Authorization: `Bearer ${token}` },
    });

console.log('Respuesta creación/edición actividad:', response.data);

const activityId = isEditMode ? id : response.data?.id;

if (!activityId) {
  console.error('No se recibió ID de actividad para subir fotos:', response.data);
  toast.error('La actividad se guardó, pero no se recibió el ID para subir fotos.');
  return;
}

await uploadPhotosForActivity(activityId);

toast.success(
  isEditMode
    ? 'Actividad actualizada correctamente.'
    : 'Actividad registrada correctamente.'
);

navigate('/admin');

    } catch (error) {
      console.error('STATUS:', error.response?.status);
      console.error('Payload real de actividad:', payload);
      console.error(
        'Detalle backend completo:',
        JSON.stringify(error.response?.data, null, 2)
      );

      const details = error.response?.data?.detail;

      if (Array.isArray(details)) {
        details.forEach((item, index) => {
          console.error(`Error ${index + 1}:`, item);
        });
      }

      toast.error(
        Array.isArray(details)
          ? details.map((d) => `${d.loc?.join(' > ')}: ${d.msg}`).join(' | ')
          : details || 'Ocurrió un error al guardar la actividad.'
      );
    } finally {
      setLoading(false);
    }
  };

  if (catalogLoading) {
    return (
      <div className="min-h-screen bg-[#F5F0E1] py-8">
        <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
          <Card>
            <CardContent className="py-10">
              <p className="text-center text-slate-600">Cargando catálogos...</p>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F5F0E1] py-8" data-testid="cargar-actividad">
      <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-slate-800">
            {isEditMode ? 'Editar Actividad' : 'Cargar Actividad'}
          </h1>
          <p className="mt-2 text-slate-600">
            {isEditMode
              ? 'Actualiza la información de la actividad registrada.'
              : 'Registra una actividad institucional con enfoque territorial, operativo y de resultados.'}
          </p>
        </div>

        <Card
          className={`border ${deadlineStatus.isClosed
              ? 'border-slate-400 bg-slate-100'
              : deadlineStatus.isWarning
                ? 'border-orange-300 bg-orange-50'
                : 'border-emerald-300 bg-emerald-50'
            }`}
        >
          <CardContent className="py-4">
            <div className="flex items-start gap-3">
              {deadlineStatus.isClosed ? (
                <AlertTriangle className="mt-0.5 h-5 w-5 text-slate-700" />
              ) : deadlineStatus.isWarning ? (
                <AlertTriangle className="mt-0.5 h-5 w-5 text-orange-700" />
              ) : (
                <ShieldAlert className="mt-0.5 h-5 w-5 text-emerald-700" />
              )}
              <div>
                <p className="font-medium text-slate-900">Aviso de cierre semanal</p>
                <p className="text-sm text-slate-700">{deadlineStatus.deadlineText}</p>
                <p className="mt-1 text-sm text-slate-700">{deadlineStatus.remainingText}</p>
                <p className="mt-2 text-xs text-slate-600">
                  Después del deadline, los registros nuevos se considerarían captura tardía.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <form onSubmit={handleSubmit} className="mt-6 space-y-6">
          <Card className="border border-[#D4C5A5]/30">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MapPin className="h-5 w-5 text-[#8B1C4A]" />
                1. Datos territoriales
              </CardTitle>
              <CardDescription>Ubicación y responsable de la actividad.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label>Alcaldía *</Label>
                  <Select
                    value={formData.alcaldia_id}
                    onValueChange={(value) => updateField('alcaldia_id', value)}
                  >
                    <SelectTrigger className={errors.alcaldia_id ? 'border-red-500' : ''}>
                      <SelectValue placeholder="Selecciona una alcaldía" />
                    </SelectTrigger>
                    <SelectContent>
                      {alcaldias.map((item) => (
                        <SelectItem key={item.id} value={String(item.id)}>
                          {item.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {errors.alcaldia_id ? (
                    <p className="text-sm text-red-600">{errors.alcaldia_id}</p>
                  ) : null}
                </div>

                <div className="space-y-2">
                  <Label>Territorio *</Label>
                  <Select
                    value={formData.territorio_id}
                    onValueChange={(value) => updateField('territorio_id', value)}
                    disabled={!formData.alcaldia_id}
                  >
                    <SelectTrigger className={errors.territorio_id ? 'border-red-500' : ''}>
                      <SelectValue
                        placeholder={
                          formData.alcaldia_id
                            ? 'Selecciona un territorio'
                            : 'Primero selecciona alcaldía'
                        }
                      />
                    </SelectTrigger>
                    <SelectContent>
                      {territorios.map((item) => (
                        <SelectItem key={item.id} value={String(item.id)}>
                          {item.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {errors.territorio_id ? (
                    <p className="text-sm text-red-600">{errors.territorio_id}</p>
                  ) : null}
                </div>
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label>Polígono *</Label>
                  <Select
                    value={formData.poligono_id}
                    onValueChange={(value) => updateField('poligono_id', value)}
                    disabled={!formData.territorio_id || poligonos.length === 0}
                  >
                    <SelectTrigger className={errors.poligono_id ? 'border-red-500' : ''}>
                      <SelectValue
                        placeholder={
                          !formData.territorio_id
                            ? 'Primero selecciona territorio'
                            : poligonos.length
                              ? 'Selecciona un polígono'
                              : 'No hay polígonos disponibles'
                        }
                      />
                    </SelectTrigger>
                    <SelectContent>
                      {poligonos.map((item) => (
                        <SelectItem key={item.id} value={String(item.id)}>
                          {item.name || `Polígono ${String(item.id).slice(0, 8)}`}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  {errors.poligono_id ? (
                    <p className="text-sm text-red-600">{errors.poligono_id}</p>
                  ) : null}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="calle_referencia">Calle o referencia *</Label>
                  <Input
                    id="calle_referencia"
                    placeholder="Ej. Av. Principal esquina con Calle 3"
                    value={formData.calle_referencia}
                    onChange={(event) => updateField('calle_referencia', event.target.value)}
                  />
                  {errors.calle_referencia ? (
                    <p className="text-sm text-red-600">{errors.calle_referencia}</p>
                  ) : null}
                </div>
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="start_date">Fecha de inicio *</Label>
                  <Input
                    id="start_date"
                    type="date"
                    value={formData.start_date}
                    onChange={(event) => updateField('start_date', event.target.value)}
                    className={errors.start_date ? 'border-red-500' : ''}
                  />
                  {errors.start_date ? (
                    <p className="text-sm text-red-600">{errors.start_date}</p>
                  ) : null}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="end_date">Fecha de cierre *</Label>
                  <Input
                    id="end_date"
                    type="date"
                    value={formData.end_date}
                    min={formData.start_date || undefined}
                    onChange={(event) => updateField('end_date', event.target.value)}
                    className={errors.end_date ? 'border-red-500' : ''}
                  />
                  {errors.end_date ? (
                    <p className="text-sm text-red-600">{errors.end_date}</p>
                  ) : null}
                </div>
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="responsable_nombre">Enlace responsable *</Label>
                  <Input
                    id="responsable_nombre"
                    value={formData.responsable_nombre}
                    readOnly
                    className={`bg-slate-100 ${errors.responsable_nombre ? 'border-red-500' : ''}`}
                  />
                  <p className="text-xs text-slate-500">
                    Se llena automáticamente con base en el usuario autenticado.
                  </p>
                  {!formData.responsable_nombre ? (
                    <p className="text-xs text-amber-600">
                      No se pudo obtener el nombre del usuario autenticado.
                    </p>
                  ) : null}
                  {errors.responsable_nombre ? (
                    <p className="text-sm text-red-600">{errors.responsable_nombre}</p>
                  ) : null}
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border border-[#D4C5A5]/30">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5 text-[#8B1C4A]" />
                2. Datos institucionales y operativos
              </CardTitle>
              <CardDescription>
                Clasificación institucional, líneas y descripción de la acción.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label>Institución principal *</Label>
                  <Input
                    value={
                      instituciones.find(
                        (item) => String(item.id) === String(formData.institucion_principal_id)
                      )?.name || ''
                    }
                    readOnly
                    className={`bg-slate-100 ${errors.institucion_principal_id ? 'border-red-500' : ''}`}
                  />
                  <p className="text-xs text-slate-500">
                    Se llena automáticamente con base en la dependencia del usuario autenticado.
                  </p>
                  {!formData.institucion_principal_id ? (
                    <p className="text-xs text-amber-600">
                      El usuario autenticado no tiene dependencia asignada.
                    </p>
                  ) : null}
                  {errors.institucion_principal_id ? (
                    <p className="text-sm text-red-600">{errors.institucion_principal_id}</p>
                  ) : null}
                </div>

                <div className="space-y-2">
                  <Label>Tipo de actividad *</Label>
                  <Select
                    value={formData.tipo_actividad_id}
                    onValueChange={(value) => updateField('tipo_actividad_id', value)}
                  >
                    <SelectTrigger className={errors.tipo_actividad_id ? 'border-red-500' : ''}>
                      <SelectValue placeholder="Selecciona un tipo" />
                    </SelectTrigger>
                    <SelectContent>
                      {tiposActividad.map((item) => (
                        <SelectItem key={item.id} value={String(item.id)}>
                          {item.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {errors.tipo_actividad_id ? (
                    <p className="text-sm text-red-600">{errors.tipo_actividad_id}</p>
                  ) : null}
                </div>
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label>Línea estratégica *</Label>
                  <Select
                    value={formData.linea_principal_id}
                    onValueChange={(value) => updateField('linea_principal_id', value)}
                  >
                    <SelectTrigger className={errors.linea_principal_id ? 'border-red-500' : ''}>
                      <SelectValue placeholder="Selecciona una línea estratégica" />
                    </SelectTrigger>
                    <SelectContent>
                      {lineasPrincipales.map((item) => (
                        <SelectItem key={item.id} value={String(item.id)}>
                          {item.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {errors.linea_principal_id ? (
                    <p className="text-sm text-red-600">{errors.linea_principal_id}</p>
                  ) : null}
                </div>

                {isVisita ? (
                  <div className="space-y-2">
                    <Label>Tipo de visita *</Label>
                    <Select
                      value={formData.tipo_visita_id}
                      onValueChange={(value) => updateField('tipo_visita_id', value)}
                    >
                      <SelectTrigger className={errors.tipo_visita_id ? 'border-red-500' : ''}>
                        <SelectValue placeholder="Selecciona el tipo de visita" />
                      </SelectTrigger>
                      <SelectContent>
                        {tiposVisita.map((item) => (
                          <SelectItem key={item.id} value={String(item.id)}>
                            {item.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {errors.tipo_visita_id ? (
                      <p className="text-sm text-red-600">{errors.tipo_visita_id}</p>
                    ) : null}
                  </div>
                ) : (
                  <div />
                )}
              </div>

              <MultiSelectChecklist
                label="Líneas de acción / ramas *"
                items={lineasRamificadasDisponibles}
                values={formData.lineas_ramificadas_ids}
                onToggle={(value) =>
                  updateField('lineas_ramificadas_ids', [value])
                }
                helperText={
                  lineasRamificadasDisponibles.length
                    ? 'Solo puedes elegir una.'
                    : 'Selecciona primero una línea estratégica.'
                }
                error={errors.lineas_ramificadas_ids}
                disabled={!formData.linea_principal_id}
              />

              <div className="space-y-2">
                <Label>Título *</Label>
                <Select
                  value={formData.title_id}
                  onValueChange={(value) => updateField('title_id', value)}
                  disabled={!formData.institucion_principal_id}
                >
                  <SelectTrigger className={errors.title_id ? 'border-red-500' : ''}>
                    <SelectValue placeholder="Selecciona un título" />
                  </SelectTrigger>
                  <SelectContent>
                    {titulosDisponibles.map((item) => (
                      <SelectItem key={item.id} value={String(item.id)}>
                        {item.name}
                      </SelectItem>
                    ))}
                    <SelectItem value={OTRO_TITULO}>Otro</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-500">
                  {formData.institucion_principal_id
                    ? 'Títulos del catálogo de tu dependencia. Elige "Otro" para capturar uno distinto.'
                    : 'Se habilita cuando el usuario tiene dependencia asignada.'}
                </p>
                {errors.title_id ? (
                  <p className="text-sm text-red-600">{errors.title_id}</p>
                ) : null}
              </div>

              {formData.title_id === OTRO_TITULO ? (
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="title">Título personalizado *</Label>
                    <span className="text-xs text-slate-500">
                      {getTrimmedLength(formData.title)}/{TITLE_MAX}
                    </span>
                  </div>
                  <Input
                    id="title"
                    value={formData.title}
                    placeholder="Ej. Jornada integral en territorio"
                    onChange={(event) =>
                      updateField('title', event.target.value.slice(0, TITLE_MAX))
                    }
                    className={errors.title ? 'border-red-500' : ''}
                  />
                  {errors.title ? <p className="text-sm text-red-600">{errors.title}</p> : null}
                </div>
              ) : null}

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="description">Descripción *</Label>
                  <span className="text-xs text-slate-500">
                    {getTrimmedLength(formData.description)}/{DESCRIPTION_MAX}
                  </span>
                </div>
                <Textarea
                  id="description"
                  rows={3}
                  value={formData.description}
                  placeholder="Describe brevemente la acción realizada"
                  onChange={(event) =>
                    updateField('description', event.target.value.slice(0, DESCRIPTION_MAX))
                  }
                  className={errors.description ? 'border-red-500' : ''}
                />
                {errors.description ? (
                  <p className="text-sm text-red-600">{errors.description}</p>
                ) : null}
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="detalle_actividad">Detalle de la actividad</Label>
                  <span className="text-xs text-slate-500">
                    {getTrimmedLength(formData.detalle_actividad)}/{DETAIL_MAX}
                  </span>
                </div>
                <Textarea
                  id="detalle_actividad"
                  rows={4}
                  value={formData.detalle_actividad}
                  placeholder="Información complementaria, contexto u observaciones puntuales."
                  onChange={(event) =>
                    updateField('detalle_actividad', event.target.value.slice(0, DETAIL_MAX))
                  }
                  className={errors.detalle_actividad ? 'border-red-500' : ''}
                />
                {errors.detalle_actividad ? (
                  <p className="text-sm text-red-600">{errors.detalle_actividad}</p>
                ) : null}
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label>¿Fue actividad conjunta?</Label>
                  <Select
                    value={formData.actividad_conjunta}
                    onValueChange={(value) => updateField('actividad_conjunta', value)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="NO">No</SelectItem>
                      <SelectItem value="SÍ">Sí</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {formData.actividad_conjunta === 'SÍ' ? (
                <div className="space-y-4 rounded-xl border border-slate-200 bg-slate-50/70 p-4">
                 <div className="grid gap-3 md:grid-cols-[minmax(0,1fr)_auto]">
  <div className="space-y-2 min-w-0">
    <Label>Agregar dependencia participante *</Label>
    <Select
  value={formData.institucion_participante_temp}
  onValueChange={(value) => updateField('institucion_participante_temp', value)}
  disabled={
    formData.instituciones_participantes_ids.length >=
    MAX_INSTITUCIONES_PARTICIPANTES
  }
>
  <SelectTrigger className="w-full min-w-0">
    <SelectValue placeholder="Selecciona una dependencia" />
  </SelectTrigger>

 <SelectContent
  position="popper"
  sideOffset={4}
  align="start"
  className="w-[min(var(--radix-select-trigger-width),calc(100vw-2rem))] max-w-[calc(100vw-2rem)]"
>
    {institucionesParticipantesDisponibles.map((item) => (
     <SelectItem
  key={item.id}
  value={String(item.id)}
  title={item.name}
  className="max-w-full h-auto py-2"
>
  <span className="block w-full whitespace-normal break-words leading-snug">
    {item.name}
  </span>
</SelectItem>
    ))}
  </SelectContent>
</Select>
  </div>

  <div className="flex items-end">
    <Button
      type="button"
      variant="outline"
      onClick={addInstitucionParticipante}
      disabled={
        !formData.institucion_participante_temp ||
        formData.instituciones_participantes_ids.length >=
          MAX_INSTITUCIONES_PARTICIPANTES
      }
      className="w-full md:w-auto"
    >
      Agregar
    </Button>
  </div>
</div>

                  <p className="text-xs text-slate-500">
                    Debes agregar al menos 1 y máximo {MAX_INSTITUCIONES_PARTICIPANTES} dependencias participantes.
                  </p>

                  {errors.instituciones_participantes_ids ? (
                    <p className="text-sm text-red-600">{errors.instituciones_participantes_ids}</p>
                  ) : null}

                  <SelectedInstituciones
                    ids={formData.instituciones_participantes_ids}
                    instituciones={instituciones}
                    onRemove={removeInstitucionParticipante}
                  />
                </div>
              ) : null}
            </CardContent>
          </Card>

          <Card className="border border-[#D4C5A5]/30">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="h-5 w-5 text-[#235B4E]" />
                3. Alcance y resultados
              </CardTitle>
              <CardDescription>
                Población objetivo, métricas, resultados y evidencia fotográfica.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {isFeriaFestival ? (
                <MultiSelectDropdown
                  label="Poblaciones objetivo *"
                  items={poblaciones}
                  values={formData.poblaciones_objetivo_ids}
                  onToggle={(value) =>
                    updateField(
                      'poblaciones_objetivo_ids',
                      formData.poblaciones_objetivo_ids.includes(value)
                        ? formData.poblaciones_objetivo_ids.filter((item) => item !== value)
                        : [...formData.poblaciones_objetivo_ids, value]
                    )
                  }
                  onSelectAll={() =>
                    updateField(
                      'poblaciones_objetivo_ids',
                      poblaciones.map((item) => String(item.id))
                    )
                  }
                  onClearAll={() => updateField('poblaciones_objetivo_ids', [])}
                  helperText="En feria/festival puedes elegir varias poblaciones."
                  error={errors.poblaciones_objetivo_ids}
                  placeholder="Selecciona una o varias poblaciones"
                />
              ) : (
                <div className="space-y-2 md:max-w-xl">
                  <Label>Población objetivo *</Label>
                  <Select
                    value={formData.poblacion_objetivo_id}
                    onValueChange={(value) => updateField('poblacion_objetivo_id', value)}
                  >
                    <SelectTrigger className={errors.poblacion_objetivo_id ? 'border-red-500' : ''}>
                      <SelectValue placeholder="Selecciona una población" />
                    </SelectTrigger>
                    <SelectContent>
                      {poblaciones.map((item) => (
                        <SelectItem key={item.id} value={String(item.id)}>
                          {item.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {errors.poblacion_objetivo_id ? (
                    <p className="text-sm text-red-600">{errors.poblacion_objetivo_id}</p>
                  ) : null}
                </div>
              )}

              <div className="grid gap-4 md:grid-cols-4">
                <div className="space-y-2">
                  <Label htmlFor="total_visitas">Total de visitas</Label>
                  <Input
                    id="total_visitas"
                    type="number"
                    min="0"
                    value={formData.total_visitas}
                    onChange={(event) => updateField('total_visitas', event.target.value)}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="total_asistentes">Total de asistentes</Label>
                  <Input
                    id="total_asistentes"
                    type="number"
                    min="0"
                    value={formData.total_asistentes}
                    onChange={(event) => updateField('total_asistentes', event.target.value)}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="total_beneficiados">Total de beneficiados *</Label>
                  <Input
                    id="total_beneficiados"
                    type="number"
                    min="0"
                    placeholder="Escribe 0 si no hubo beneficiados"
                    value={formData.total_beneficiados}
                    onChange={(event) => updateField('total_beneficiados', event.target.value)}
                    className={errors.metricas ? 'border-red-500' : ''}
                  />
                  <p className="text-xs text-slate-500">
                    Este campo es obligatorio. No puede quedar vacío; si no hubo beneficiados, escribe 0.
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="progress_percentage">Porcentaje de avance</Label>
                  <div className="relative">
                    <Input
                      id="progress_percentage"
                      type="number"
                      min="0"
                      max="100"
                      placeholder={isObraProyecto ? '0 a 100' : 'Solo obras y proyectos'}
                      value={formData.progress_percentage}
                      disabled={!isObraProyecto}
                      onChange={(event) => {
                        const raw = event.target.value;

                        if (raw === '') {
                          updateField('progress_percentage', '');
                          return;
                        }

                        const numeric = Number(raw);

                        if (Number.isNaN(numeric)) return;
                        if (numeric < 0) {
                          updateField('progress_percentage', '0');
                          return;
                        }
                        if (numeric > 100) {
                          updateField('progress_percentage', '100');
                          return;
                        }

                        updateField('progress_percentage', raw);
                      }}
                      className={`pr-10 ${errors.metricas ? 'border-red-500' : ''}`}
                    />
                    <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-sm text-slate-500">
                      %
                    </span>
                  </div>
                  <p className="text-xs text-slate-500">
                    Solo aplica para obras y proyectos. Debe estar entre 0 y 100.
                  </p>
                </div>
              </div>

              {errors.metricas ? <p className="text-sm text-red-600">{errors.metricas}</p> : null}

              <div className="space-y-2">
                <Label htmlFor="resultados">Resultados obtenidos *</Label>
                <Textarea
                  id="resultados"
                  rows={4}
                  value={formData.resultados}
                  placeholder="Describe los resultados cualitativos y cuantitativos obtenidos"
                  onChange={(event) => updateField('resultados', event.target.value)}
                  className={errors.resultados ? 'border-red-500' : ''}
                />
                {errors.resultados ? <p className="text-sm text-red-600">{errors.resultados}</p> : null}
              </div>

              <div className="space-y-2">
                <Label htmlFor="observaciones">Observaciones</Label>
                <Textarea
                  id="observaciones"
                  rows={3}
                  value={formData.observaciones}
                  placeholder="Notas adicionales o eventualidades relevantes"
                  onChange={(event) => updateField('observaciones', event.target.value)}
                />
              </div>

              <div className="space-y-3 border-t pt-4">
                <div className="flex items-center justify-between gap-2">
                  <Label className="flex items-center gap-2">
                    <ImageIcon className="h-4 w-4 text-[#8B1C4A]" />
                    Evidencia fotográfica *
                  </Label>
                  <span className="text-xs text-slate-500">
                    {photoCount}/{MAX_PHOTOS}
                  </span>
                </div>

                <p className="text-xs text-slate-500">
                  Mínimo 1 foto y máximo {MAX_PHOTOS}. Formatos permitidos: JPG, JPEG, PNG, WEBP.
                  Máximo {MAX_IMAGE_SIZE_MB}MB por archivo.
                </p>

                {errors.photos ? <p className="text-sm text-red-600">{errors.photos}</p> : null}

                <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
                  {uploadedPhotos.map((photo) => (
                    <div
                      key={photo.id}
                      className="group relative aspect-square overflow-hidden rounded-lg border bg-slate-100"
                    >
                      <img
                        src={photo.preview}
                        alt={photo.name}
                        className="h-full w-full object-cover"
                      />
                      <button
                        type="button"
                        onClick={() => removePhoto(photo.id)}
                        className="absolute right-2 top-2 rounded-full bg-red-500 p-1 text-white opacity-100 transition-opacity sm:opacity-0 sm:group-hover:opacity-100"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  ))}

                  {uploadedPhotos.length < MAX_PHOTOS ? (
                    <label className="flex aspect-square cursor-pointer flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed border-slate-300 p-3 text-center transition-colors hover:border-[#8B1C4A]">
                      <Plus className="h-8 w-8 text-slate-400" />
                      <span className="text-sm text-slate-500">Agregar foto</span>
                      <input
                        type="file"
                        multiple
                        accept=".jpg,.jpeg,.png,.webp,image/jpeg,image/png,image/webp"
                        className="hidden"
                        onChange={handlePhotoChange}
                      />
                    </label>
                  ) : null}
                </div>
              </div>
            </CardContent>
          </Card>

          <div className="flex flex-col justify-end gap-4 sm:flex-row">
            <Button type="button" variant="outline" onClick={() => navigate('/')} disabled={loading}>
              Cancelar
            </Button>
            <Button type="submit" disabled={loading} className="bg-[#8B1C4A] hover:bg-[#70163A]">
              {loading ? (
                <span className="flex items-center gap-2">
                  <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white" />
                  Guardando...
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <Check className="h-4 w-4" />
                  {isEditMode ? 'Actualizar actividad' : 'Cargar actividad'}
                </span>
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CargarActividad;